import React, { FunctionComponent, ReactNode, useContext, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import Translate from 'ui/atoms/translate';
import Currency from 'ui/atoms/currency';
import Icon from 'ui/atoms/icon';
import { IssuerInvestmentStatusIndicator } from 'src/apps/issuer/shared/issuer-investment-status';
import { PaginationProps } from 'ui/molecules/pagination';
import { I18NContext } from 'src/ui/i18n/provider';
import {
  AdminApi,
  AdminInvestmentsListOrderingEnum,
  AdminInvestmentsValuesRetrieveFieldEnum,
  FrontendStatusEnum,
} from 'src/api';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import { Search } from 'src/ui/molecules/filtering';

export interface StudioInvestmentsTableProps {
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  investments?: any;
  goToDetails: (productId: string, investmentId: string) => void;
  title?: ReactNode;
  loading?: boolean;
  paginationProps?: PaginationProps;
  hasPermission?: boolean;
  hidePaginationNavigation?: boolean;
  setFilterPopupHeight?: (height: number) => void;
  campaignId?: string;
  investorId?: string;
}

const StudioInvestmentsTable: FunctionComponent<StudioInvestmentsTableProps> = (props) => {
  const {
    ordering,
    onOrderBy,
    investments,
    goToDetails,
    title,
    loading,
    hasPermission = true,
    paginationProps,
    hidePaginationNavigation,
    setFilterPopupHeight,
    campaignId,
    investorId,
  } = props;
  const { activeLocale } = useContext(I18NContext);

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const investmentStatusFilterOptions = useMemo(() => {
    return Object.values(FrontendStatusEnum).map((status) => ({
      id: status,
      name: translate(`issuerInvestmentStatus.${snakeToCamelCaseWithNumbers(status)}.status`),
    }));
  }, [translate]);

  return (
    <DataRecordTable
      ordering={ordering}
      onOrderBy={onOrderBy}
      data={investments}
      title={title}
      loading={loading}
      hasPermission={hasPermission}
      paginationProps={paginationProps}
      hidePaginationNavigation={hidePaginationNavigation}
      setFilterPopupHeight={setFilterPopupHeight}
      searchComponent={
        <Search input="search" defaultField="search" label={translate('filters.labels.investments')} name="search" />
      }
      cells={[
        {
          title: <Translate name={`studioInvestmentsTable.product`} />,
          render: ({ productName }) => productName,
          orderingFieldName: AdminInvestmentsListOrderingEnum.PRODUCT_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'productName',
          searchFieldName: AdminInvestmentsValuesRetrieveFieldEnum.PRODUCT_NAME,
          getFilterData: (params: any) =>
            adminApi.adminInvestmentsValuesRetrieve({
              ...params,
              ...(campaignId && { campaign: campaignId }),
              ...(investorId && { investorId: investorId }),
            }),
        },
        {
          title: <Translate name={`studioInvestmentsTable.investorName`} />,
          render: ({ investor, investorName }) => investorName || investor?.investorName,
          orderingFieldName: AdminInvestmentsListOrderingEnum.INVESTOR_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'investorName',
          searchFieldName: AdminInvestmentsValuesRetrieveFieldEnum.INVESTOR_NAME,
          getFilterData: (params: any) =>
            adminApi.adminInvestmentsValuesRetrieve({
              ...params,
              ...(campaignId && { campaign: campaignId }),
              ...(investorId && { investorId: investorId }),
            }),
        },
        {
          title: <Translate name={`studioInvestmentsTable.investmentTotal`} args={[<>&shy;</>]} />,
          alignContent: 'right',
          render: ({ investmentTotal }) => <Currency>{investmentTotal}</Currency>,
          orderingFieldName: AdminInvestmentsListOrderingEnum.INVESTMENT_TOTAL_ASC,
          type: CellTypeEnum.currency,
          filterName: 'investmentTotal',
        },
        {
          title: <Translate name={`studioInvestmentsTable.distributionPlatform`} />,
          render: ({ distributionPlatformDisplayName }) => distributionPlatformDisplayName,
          orderingFieldName: AdminInvestmentsListOrderingEnum.DISTRIBUTION_PLATFORM_DISPLAY_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'distributionPlatformDisplayName',
          searchFieldName: AdminInvestmentsValuesRetrieveFieldEnum.DISTRIBUTION_PLATFORM_DISPLAY_NAME,
          getFilterData: (params: any) =>
            adminApi.adminInvestmentsValuesRetrieve({
              ...params,
              ...(campaignId && { campaign: campaignId }),
              ...(investorId && { investorId: investorId }),
            }),
        },
        {
          title: <Translate name={`studioInvestmentsTable.status`} />,
          render: ({ frontendStatus }) => (
            <IssuerInvestmentStatusIndicator status={frontendStatus || FrontendStatusEnum.FINISHED} />
          ),
          width: activeLocale === 'de' ? 290 : 250,
          orderingFieldName: AdminInvestmentsListOrderingEnum.FRONTEND_STATUS_ASC,
          type: CellTypeEnum.text,
          filterName: 'frontendStatus',
          getFilterData: () => {
            return investmentStatusFilterOptions;
          },
        },
        {
          title: '',
          render: ({ token, id }) => (
            <Icon name="arrow-right" color="primary" onClick={() => goToDetails(token.id, id || '')} size="large" />
          ),
          alignContent: 'right',
          width: 56,
        },
      ]}
    />
  );
};

export default StudioInvestmentsTable;
